// Settings landing — Wave E follow-up.
//
// Three groups of card links:
//   Company           — profile / notifications / templates
//   Reference Data    — products / work sources / payment modes / time slots
//   Data & Compliance — audit log + data export (admin-only group)

import { Link } from 'react-router-dom';
import {
  Bell,
  BellRing,
  Building2,
  CalendarOff,
  ClipboardList,
  Clock,
  CreditCard,
  Link as LinkIcon,
  Mail,
  Package,
  Palette,
  Printer,
  UserCircle2,
} from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import type { ComponentType } from 'react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { useAuthStore } from '../../store/authStore';
import styles from './SettingsIndexPage.module.css';

interface SettingCard {
  to: string;
  label: string;
  description: string;
  icon: ComponentType<LucideProps>;
  adminOnly?: boolean;
}

interface SettingGroup {
  title: string;
  /** When set, the whole group is hidden unless the role matches. */
  adminOnly?: boolean;
  items: SettingCard[];
}

const GROUPS: SettingGroup[] = [
  {
    title: 'Company',
    items: [
      {
        to: '/settings/company',
        label: 'Company Settings',
        description: 'Profile, branding, banking, timeframes, and reminders.',
        icon: Building2,
      },
      {
        to: '/settings/holidays',
        label: 'Holidays',
        description: 'Manage bank holidays and company closure dates.',
        icon: CalendarOff,
      },
      {
        to: '/settings/theme',
        label: 'Theme',
        description: 'Customise colours used across the app. Live preview, save, reset.',
        icon: Palette,
      },
      {
        to: '/settings/notifications',
        label: 'Notification Settings',
        description: 'Default automated email and SMS reminders for collections.',
        icon: Bell,
      },
      {
        to: '/settings/templates',
        label: 'Notification Templates',
        description: 'Per-channel message templates with variable substitution.',
        icon: Mail,
      },
      {
        to: '/settings/print-templates',
        label: 'Print Templates',
        description: 'Customise diary, receipt, and invoice print/PDF templates.',
        icon: Printer,
      },
      {
        to: '/settings/quick-links',
        label: 'Quick Links',
        description: 'Configure sidebar shortcuts for your team.',
        icon: LinkIcon,
        adminOnly: true,
      },
      {
        to: '/settings/dwt',
        label: 'Digital Waste Tracking',
        description: 'Defra receipt-of-waste reporting: receiver, carrier and API code.',
        icon: LinkIcon,
        adminOnly: true,
      },
    ],
  },
  {
    title: 'Personal',
    items: [
      {
        to: '/profile',
        label: 'My Profile',
        description: 'Edit your name, email, contact details, and password.',
        icon: UserCircle2,
      },
      {
        to: '/settings/push',
        label: 'Push Notifications',
        description: 'Enable or disable browser push alerts for this device.',
        icon: BellRing,
      },
    ],
  },
  {
    title: 'Reference Data',
    items: [
      {
        to: '/settings/products',
        label: 'Products',
        description: 'Service / vehicle product catalogue.',
        icon: Package,
      },
      {
        to: '/settings/work-sources',
        label: 'Work Sources',
        description: 'Lead sources for incoming bookings.',
        icon: ClipboardList,
      },
      {
        to: '/settings/payment-modes',
        label: 'Payment Modes',
        description: 'Accepted payment methods (cash, BACS, cheque, etc.).',
        icon: CreditCard,
      },
      {
        to: '/settings/time-slots',
        label: 'Time Slots',
        description: 'AM / AM-PM / PM labels per weekday and weekend.',
        icon: Clock,
      },
    ],
  },
];

export function SettingsIndexPage(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role ?? null);
  const isAdmin = role === 'admin';

  return (
    <div className={styles.page}>
      <PageHeader
        title="Settings"
        subtitle="Manage your company, reference data, and compliance tools"
      />

      {GROUPS.map((group) => {
        if (group.adminOnly && !isAdmin) return null;
        return (
          <Card key={group.title} title={group.title}>
            <ul className={styles.grid}>
              {group.items.filter((item) => !item.adminOnly || isAdmin).map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className={styles.card}>
                    <span className={styles.icon}>
                      <item.icon size={20} aria-hidden />
                    </span>
                    <span className={styles.text}>
                      <span className={styles.label}>{item.label}</span>
                      <span className={styles.description}>{item.description}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </Card>
        );
      })}
    </div>
  );
}
